import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import FloatingPortal from '@components/ui/FloatingPortal';
import { getStoreMembers } from '../../../../services/storeManagementService';
import type { StoreMember } from '../../../../types/api.types';

interface StaffMultiSelectProps {
    storeBrandId: string;
    storeId: string;
    selectedIds: string[];
    onChange: (ids: string[]) => void;
    disabled?: boolean;
}

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 400;

function memberLabel(member: StoreMember): string {
    return member.role ? `${member.name} (${member.role})` : member.name;
}

export default function StaffMultiSelect({
    storeBrandId,
    storeId,
    selectedIds,
    onChange,
    disabled = false,
}: StaffMultiSelectProps) {
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState<StoreMember[]>([]);
    const [selectedMembers, setSelectedMembers] = useState<StoreMember[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isHydrating, setIsHydrating] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const anchorRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    useEffect(() => {
        let cancelled = false;

        const hydrateSelectedMembers = async () => {
            if (selectedIds.length === 0) {
                setSelectedMembers([]);
                return;
            }

            setIsHydrating(true);
            try {
                const members = await getStoreMembers(storeBrandId, { storeId });
                if (cancelled) return;

                const hydrated = selectedIds
                    .map((id) => members.find((member) => member._id === id))
                    .filter((member): member is StoreMember => member !== undefined);
                setSelectedMembers(hydrated);
            } catch {
                if (!cancelled) {
                    setSelectedMembers([]);
                }
            } finally {
                if (!cancelled) {
                    setIsHydrating(false);
                }
            }
        };

        hydrateSelectedMembers();

        return () => {
            cancelled = true;
        };
        // Hydrate chip labels once per store — selection changes are handled locally.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeBrandId, storeId]);

    const runSearch = useCallback(
        async (text: string) => {
            if (text.length < MIN_SEARCH_LENGTH) {
                setSearchResults([]);
                setIsSearching(false);
                return;
            }

            setIsSearching(true);
            try {
                const results = await getStoreMembers(storeBrandId, {
                    storeId,
                    searchText: text,
                });
                setSearchResults(results);
                setIsOpen(true);
            } catch {
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        },
        [storeBrandId, storeId]
    );

    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (searchText.length < MIN_SEARCH_LENGTH) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        debounceTimerRef.current = setTimeout(() => {
            runSearch(searchText);
        }, DEBOUNCE_MS);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchText, runSearch]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                anchorRef.current?.contains(target) ||
                panelRef.current?.contains(target)
            ) {
                return;
            }
            setIsOpen(false);
            setHighlightedIndex(-1);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const addMember = useCallback(
        (member: StoreMember) => {
            if (selectedIdSet.has(member._id)) return;

            setSelectedMembers((prev) => [...prev, member]);
            onChange([...selectedIds, member._id]);
            setSearchText('');
            setSearchResults([]);
            setHighlightedIndex(-1);
            inputRef.current?.focus();
        },
        [onChange, selectedIdSet, selectedIds]
    );

    const removeMember = useCallback(
        (memberId: string) => {
            setSelectedMembers((prev) => prev.filter((member) => member._id !== memberId));
            onChange(selectedIds.filter((id) => id !== memberId));
        },
        [onChange, selectedIds]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!isOpen || searchResults.length === 0) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setHighlightedIndex((prev) =>
                        prev < searchResults.length - 1 ? prev + 1 : prev
                    );
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (highlightedIndex >= 0 && searchResults[highlightedIndex]) {
                        addMember(searchResults[highlightedIndex]);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    setIsOpen(false);
                    setHighlightedIndex(-1);
                    inputRef.current?.blur();
                    break;
            }
        },
        [addMember, highlightedIndex, isOpen, searchResults]
    );

    const showHint =
        searchText.length > 0 && searchText.length < MIN_SEARCH_LENGTH && !isSearching;
    const showNoResults =
        isOpen &&
        !isSearching &&
        searchText.length >= MIN_SEARCH_LENGTH &&
        searchResults.length === 0;
    const showDropdown =
        isOpen &&
        searchText.length >= MIN_SEARCH_LENGTH &&
        (isSearching || searchResults.length > 0 || showNoResults);

    return (
        <div className="mt-3 space-y-3">
            <div className="relative">
                <div ref={anchorRef} className="relative">
                    <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8] pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchText}
                        disabled={disabled}
                        placeholder="Search staff by name or role..."
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            setHighlightedIndex(-1);
                            setIsOpen(true);
                        }}
                        onFocus={() => {
                            if (searchText.length >= MIN_SEARCH_LENGTH) {
                                setIsOpen(true);
                            }
                        }}
                        onKeyDown={handleKeyDown}
                        className="w-full pl-9 pr-10 py-2 text-sm border border-[#e2e8f0] rounded-lg bg-[#f8fafc] text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#6e64f0]/20 focus:border-[#6e64f0] disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    {isSearching && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#6e64f0]" />
                        </div>
                    )}
                </div>

                {showHint && (
                    <p className="text-xs text-[#94a3b8] mt-1.5">
                        Type at least {MIN_SEARCH_LENGTH} characters to search
                    </p>
                )}

                {showDropdown && (
                    <FloatingPortal
                        anchorRef={anchorRef}
                        panelRef={panelRef}
                        isOpen={showDropdown}
                        maxHeight={192}
                        className="bg-white shadow-lg border border-[#e2e8f0] rounded-lg overflow-y-auto"
                    >
                        {showNoResults ? (
                            <p className="px-3 py-2.5 text-sm text-[#64748b] text-center">
                                No staff match your search.
                            </p>
                        ) : (
                            <ul className="py-1">
                                {searchResults.map((member, index) => {
                                    const isSelected = selectedIdSet.has(member._id);
                                    const isHighlighted = index === highlightedIndex;

                                    return (
                                        <li key={member._id}>
                                            <button
                                                type="button"
                                                disabled={isSelected}
                                                onClick={() => addMember(member)}
                                                className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between gap-2 transition-colors ${
                                                    isSelected
                                                        ? 'text-[#94a3b8] bg-[#f8fafc] cursor-default'
                                                        : isHighlighted
                                                          ? 'bg-[#f5f3ff] text-[#6e64f0]'
                                                          : 'text-[#475569] hover:bg-[#f8fafc]'
                                                }`}
                                            >
                                                <span className="truncate">{memberLabel(member)}</span>
                                                {isSelected && (
                                                    <svg
                                                        className="w-4 h-4 shrink-0 text-[#6e64f0]"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                        aria-hidden="true"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M5 13l4 4L19 7"
                                                        />
                                                    </svg>
                                                )}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </FloatingPortal>
                )}
            </div>

            {isHydrating && selectedIds.length > 0 && selectedMembers.length === 0 ? (
                <p className="text-xs text-[#94a3b8]">Loading selected staff...</p>
            ) : selectedMembers.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                    {selectedMembers.map((member) => (
                        <span
                            key={member._id}
                            className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-[#f5f3ff] text-[#5b21b6] border border-[#ddd6fe]"
                        >
                            <span className="truncate max-w-[200px]">{memberLabel(member)}</span>
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={() => removeMember(member._id)}
                                className="shrink-0 rounded-full p-0.5 text-[#7c3aed] hover:bg-[#ede9fe] hover:text-[#5b21b6] disabled:opacity-50 disabled:cursor-not-allowed"
                                aria-label={`Remove ${member.name}`}
                            >
                                <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </span>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-[#94a3b8]">No staff selected yet. Search above to add.</p>
            )}
        </div>
    );
}
